import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@availity/element';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import useGuides from '../../../hooks/useGuides';
import { extractHeadings, extractUseCases, UseCase } from './sidebar';
import { themeColors } from '../../../static/constants/styles';
import Button from '../../Utils/components/Button';
import SidebarSkeleton from '../SidebarSkeleton';
import GuideSidebar from './GuideSidebar';

const UseCaseCard = ({ useCase, theme = 'dark' }: { useCase: UseCase; theme?: 'light' | 'dark' }) => {
  const cardColors =
    theme === 'light'
      ? {
          bg: '#F7F9FF',
          titleColor: '#000C30',
          textColor: 'rgba(17, 29, 66, 0.9)',
        }
      : {
          bg: '#191F31',
          titleColor: '#BBD5EA',
          textColor: '#EFF3FF',
        };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '24px',
        gap: '16px',
        width: '100%',
        minHeight: '204px',
        background: cardColors.bg,
        borderRadius: '8px',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: '0px',
          gap: '10px',
          width: '24px',
          height: '24px',
          borderRadius: '100px',
          flex: 'none',
          order: 0,
          flexGrow: 0,
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#FF671F">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0px',
          gap: '16px',
          width: '100%',
          flex: 1,
          order: 1,
          alignSelf: 'stretch',
          overflow: 'hidden',
        }}
      >
        <h3
          style={{
            width: '100%',
            fontFamily: 'Montserrat',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '24px',
            color: cardColors.titleColor,
            flex: 'none',
            order: 0,
            alignSelf: 'stretch',
            flexGrow: 0,
            margin: 0,
          }}
        >
          {useCase.title}
        </h3>
        <p
          style={{
            width: '100%',
            wordBreak: 'break-word',
            overflow: 'hidden',
            fontFamily: 'Lato',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '26px',
            color: cardColors.textColor,
            flex: 'none',
            order: 1,
            alignSelf: 'stretch',
            flexGrow: 0,
            margin: 0,
          }}
        >
          {useCase.description}
        </p>
      </div>
    </div>
  );
};

interface GuidesTabProps {
  theme?: 'light' | 'dark';
  sidebarOpen?: boolean;
  onSidebarClose?: () => void;
}

const GuidesTab = ({ theme = 'dark', sidebarOpen = false, onSidebarClose }: GuidesTabProps): JSX.Element => {
  const { guides, loading, error } = useGuides();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeGuide, setActiveGuide] = useState<string>(() => {
     if (guides.length > 0) return guides[0].name;
     return '';
  });
  const [activeSection, setActiveSection] = useState<string>('');
  const [expandedGuides, setExpandedGuides] = useState<Set<string>>(new Set());

  const colors =
    theme === 'light'
      ? {
          bg: '#FFFFFF',
          bgSecondary: '#EFF3FF',
          border: '#C3CAE2',
          text: '#000C30',
          textSecondary: 'rgba(17, 29, 66, 0.9)',
          textMuted: 'rgba(0, 12, 48, 0.75)',
          textHeading: '#0D2543',
          accent: '#2F3444',
          cardBg: '#F7F9FF',
          codeBg: '#EFF3FF',
          codeText: '#FF6017',
          inputBorder: '#C3CAE2',
        }
      : {
          bg: '#151823',
          bgSecondary: '#191F31',
          border: '#363C4D',
          text: '#EFF3FF',
          textSecondary: 'rgba(239, 243, 255, 0.9)',
          textMuted: '#BBD5EA',
          textHeading: '#A6C5DE',
          accent: '#2F3444',
          cardBg: '#191F31',
          codeBg: '#0E1F52',
          codeText: '#FF6017',
          inputBorder: '#363C4D',
        };

  const handleGuideClick = (name: string) => {
    setActiveGuide(name);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setExpandedGuides((prev) => {
      const next = new Set(prev);
      if (next.has(name)) { next.delete(name); } else { next.add(name); }
      return next;
    });
    onSidebarClose?.();
  };

  const handleSectionClick = (guideName: string, sectionId: string) => {
    setActiveGuide(guideName);
    setActiveSection(`${guideName}-${sectionId}`);
    onSidebarClose?.();
    setTimeout(() => {
      document.getElementById(`${guideName}-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    if (guides.length === 0) return;

    if (activeGuide && !location.state) return;

    const scrollToTag = (location.state as { scrollToTag?: string } | null)?.scrollToTag;
    let targetGuide = guides.find(g => g.name === activeGuide) || guides[0];

    if (scrollToTag) {
      const normalizedTag = scrollToTag.toLowerCase().replace(/[\s_-]+/g, '');
      const matchingGuide = guides.find(g => {
        const normalizedName = g.name.toLowerCase().replace(/[\s_-]+/g, '');
        return normalizedName === normalizedTag || 
               normalizedName.includes(normalizedTag) ||
               normalizedTag.includes(normalizedName);
      });
      if (matchingGuide) {
        targetGuide = matchingGuide;
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'auto' }), 100);
      }
    } else if (!activeGuide) {
       // If no active guide and no scroll tag, default to first guide
       targetGuide = guides[0];
    }

    setActiveGuide(targetGuide.name);
    setExpandedGuides(new Set([targetGuide.name]));
  }, [guides, activeGuide, location.state]);

  useEffect(() => {
    const handleScroll = () => {
      const currentGuide = guides.find((g) => g.name === activeGuide);
      if (!currentGuide) return;

      const headings = extractHeadings(currentGuide.content);
      const sections = headings.map((h) => `${currentGuide.name}-${h.id}`);

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element && element.getBoundingClientRect().top <= 150) {
          setActiveSection(sections[i]);
          return;
        }
      }
      setActiveSection('');
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [guides, activeGuide]);

  if (loading) {
    return (
      <Box
        className="guides-loading"
        sx={{
          display: 'flex',
          minHeight: 'calc(100vh - 120px)',
          bgcolor: colors.bg,
          position: 'relative',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'center',
        }}
      >
        <SidebarSkeleton theme={theme} />
        <Box
          className="guides-loading__container"
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: '32px',
            flex: 1,
            justifyContent: 'center',
          }}
        >
          <Box
            className="guides-loading__content"
            sx={{
              flex: 1,
              p: { xs: '24px 16px', sm: '40px 24px', md: '64px 32px', lg: '64px 64px' },
              bgcolor: colors.bg,
              maxWidth: { xs: '100%', sm: '688px', md: '80%' },
              mx: { xs: 'auto', sm: 'auto', md: 0 },
              width: '100%',
            }}
          >
            <Box className="guides-loading__line guides-loading__line--header" sx={{ height: 40, bgcolor: colors.bgSecondary, mb: 4, borderRadius: '4px', width: '60%' }} />
            <Box className="guides-loading__line" sx={{ height: 20, bgcolor: colors.bgSecondary, mb: 2, borderRadius: '4px' }} />
            <Box className="guides-loading__line" sx={{ height: 20, bgcolor: colors.bgSecondary, mb: 2, borderRadius: '4px' }} />
            <Box className="guides-loading__line guides-loading__line--short" sx={{ height: 20, bgcolor: colors.bgSecondary, mb: 2, borderRadius: '4px', width: '80%' }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography>Error loading guides: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      data-theme={theme}
      sx={{
        display: 'flex',
        minHeight: 'calc(100vh - 120px)',
        bgcolor: colors.bg,
        position: 'relative',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'center',
      }}
    >
      <GuideSidebar
        guides={guides}
        activeGuide={activeGuide}
        activeSection={activeSection}
        expandedGuides={expandedGuides}
        theme={theme}
        sidebarOpen={sidebarOpen}
        onSidebarClose={onSidebarClose}
        onGuideClick={handleGuideClick}
        onSectionClick={handleSectionClick}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: '32px',
          flex: 1,
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            flex: 1,
            p: { xs: '24px 16px', sm: '40px 24px', md: '64px 32px', lg: '64px 0px' },
            bgcolor: colors.bg,
            color: colors.text,
            fontFamily: 'Lato',
            maxWidth: { xs: '100%', sm: '688px', md: '80%' },
            mx: { xs: 'auto', sm: 'auto', md: 0 },
            width: '100%',
            '& h1': {
              fontSize: 16,
              fontWeight: 600,
              lineHeight: '19px',
              mb: 2,
              mt: 0,
              color: colors.textMuted,
              fontFamily: 'Lato',
            },
            '& h2': {
              fontSize: { xs: 28, md: 28 },
              fontWeight: 600,
              lineHeight: '40px',
              mt: { xs: 3, md: 4 },
              mb: 2,
              pt: { xs: 2, md: 3 },
              color: colors.text,
              fontFamily: 'Lato',
              borderTop: `1px solid ${colors.border}`,
              '&:first-of-type': {
                borderTop: 'none',
                pt: 0,
                mt: 0,
              },
            },
            '& h3': {
              fontSize: 20,
              fontWeight: 600,
              lineHeight: '24px',
              mt: 3,
              mb: 2,
              color: colors.textMuted,
              fontFamily: 'Montserrat',
            },
            '& h3 + p, & h3 ~ p': {
              ml: 3,
            },
            '& h3 ~ ul, & h3 ~ ol': {
              ml: 3,
            },
            '& p': {
              fontSize: 16,
              fontWeight: 400,
              lineHeight: '26px',
              mb: 2,
              color: colors.text,
              fontFamily: 'Lato',
            },
            '& ul, & ol': {
              fontSize: 16,
              lineHeight: '26px',
              mb: 2,
              pl: 4,
              color: colors.textSecondary,
              fontFamily: 'Lato',
              '& li': {
                mb: 1,
              },
            },
            '& code': {
              bgcolor: colors.codeBg,
              p: '3px 8px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '12px',
              color: colors.codeText,
              border: `1px solid ${colors.border}`,
            },
            '& pre': {
              bgcolor: colors.codeBg,
              p: 2,
              borderRadius: '6px',
              overflow: 'auto',
              mb: 2,
              border: `1px solid ${colors.border}`,
              '& code': {
                bgcolor: 'transparent',
                p: 0,
                border: 'none',
              },
            },
            '& strong': {
              fontWeight: 700,
              color: colors.text,
              fontFamily: 'Lato',
            },
            '& em': {
              fontStyle: 'italic',
              color: colors.textMuted,
            },
            '& blockquote': {
              borderLeft: theme === 'light' ? '4px solid #519EE6' : '4px solid #25376F',
              pl: 2,
              ml: 0,
              color: colors.textMuted,
              fontStyle: 'italic',
            },
          }}
        >
          {guides
            .filter((guide) => guide.name === activeGuide)
            .map((guide) => (
              <Box key={guide.name} id={guide.name}>
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => {
                      const text = String(children);
                      const id = text.toLowerCase().replace(/[^\da-z]+/g, '-');
                      return <h1 id={`${guide.name}-${id}`}>{children}</h1>;
                    },
                    h2: ({ children }) => {
                      const text = String(children);
                      const id = text.toLowerCase().replace(/[^\da-z]+/g, '-');
                      if (
                        String(children) === 'Real-time Use Cases' ||
                        String(children) === 'Realtime Use Cases' ||
                        String(children) === 'Who Benefits'
                      ) {
                        const useCases = extractUseCases(guide.content);
                        return (
                          <>
                            <h2 id={`${guide.name}-${id}`}>{children}</h2>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                gap: '16px',
                                marginTop: '24px',
                              }}
                            >
                              {useCases.map((useCase) => (
                                <UseCaseCard key={useCase.id} useCase={useCase} theme={theme} />
                              ))}
                            </div>
                          </>
                        );
                      }
                      return <h2 id={`${guide.name}-${id}`}>{children}</h2>;
                    },
                    h3: ({ children }) => {
                      const text = String(children);
                      const id = text.toLowerCase().replace(/[^\da-z]+/g, '-');
                      return <h3 id={`${guide.name}-${id}`}>{children}</h3>;
                    },
                  }}
                >
                  {guide.content.replace(
                    /## (?:Real-time Use Cases|Realtime Use Cases|Who Benefits)([\S\s]*?)(?=##|$)/g,
                    (match, section) => {
                      // Remove bullet lists that match the use case pattern from the section
                      const cleanedSection = section.replace(/^\s*-\s+\*\*.+\*\*\s+-\s+.+$/gm, '');
                      return match.replace(section, cleanedSection);
                    }
                  )}
                </ReactMarkdown>
              </Box>
            ))}
        </Box>
      </Box>
      <Box
        sx={{
          position: { xs: 'static', lg: 'sticky' },
          top: '220px',
          width: { lg: '199px' },
          height: 'fit-content',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignSelf: 'flex-start',
          ml: { xs: 0, lg: '32px' },
          mr: { xs: 0, lg: '64px' },
          p: { xs: '24px 16px', sm: '30px 80px', lg: 0 },
          order: { xs: 3, lg: 0 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box
            component="h3"
            sx={{
              fontFamily: 'Lato',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '19px',
              color: colors.textHeading,
              m: 0,
            }}
          >
            Ready to Connect?
          </Box>
          <Button 
            variant="navbar-signup"
            onClick={() => {
              navigate(`/docs/api-spec#tag/${activeGuide}`);
            }}
            sx={{ whiteSpace: 'nowrap' }}
          >
            View Documentation
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default GuidesTab;
